import { Box, Typography } from '@mui/material';

import { COLORS } from '../../IndividualPlan/tokens';
import type { PeDataCoverage, PeReportSettings, PeSettingSource } from '../types';
import { CountLink } from './CountLink';

const LABEL_SX = {
  m: 0,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: '24px',
  color: COLORS.muted,
  textTransform: 'capitalize',
} as const;
const LINE_SX = {
  m: 0,
  fontSize: 16,
  fontWeight: 500,
  lineHeight: '24px',
  color: COLORS.ink,
} as const;
const FOOT_SX = {
  m: 0,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  color: COLORS.muted,
} as const;

const EMPTY_LINE = 'Every roster row was read as uploaded; nothing moved a student out of a count.';

function defaultTag(source: PeSettingSource | undefined): string {
  if (source === 'district') return '';
  return source === 'operator' ? ' (set for this run)' : ' (default)';
}

export function settingsFooter(settings: PeReportSettings): string {
  const sources = settings.sources;
  return (
    `Counted with the district's settings: newcomer for the first ${settings.newcomerMaxYears} years${defaultTag(sources?.newcomerMaxYears)}` +
    ` · long-term from year ${settings.ltelMinYears}${defaultTag(sources?.ltelMinYears)}` +
    ` · monitoring for ${settings.monitoringYears} years after exit${defaultTag(sources?.monitoringYears)}.` +
    ' Each count opens the students behind it.'
  );
}

export function AboutTheseCounts({
  coverage,
  settings,
}: {
  coverage: PeDataCoverage | undefined;
  settings: PeReportSettings;
}) {
  if (!coverage) return null;
  return (
    <Box data-testid="about-these-counts" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography component="p" sx={LABEL_SX}>
        About these counts
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {coverage.lines.length === 0 ? (
          <Typography component="p" sx={{ ...LINE_SX, color: COLORS.muted }}>
            {EMPTY_LINE}
          </Typography>
        ) : (
          coverage.lines.map((line) => (
            <Typography key={line.key} component="p" data-testid="coverage-line" sx={LINE_SX}>
              <CountLink
                count={line.count}
                listKey={`note:${line.key}`}
              >{`${line.count} ${line.noun}`}</CountLink>
              {` ${line.text}`}
            </Typography>
          ))
        )}
      </Box>
      <Typography component="p" data-testid="coverage-footer" sx={FOOT_SX}>
        {settingsFooter(settings)}
      </Typography>
    </Box>
  );
}
